import { useMutation } from '@tanstack/react-query'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'
import { invalidateRecordAuthorization } from '../recordAuthorizationQuery'
import { cancelRecordGrants, invalidateRecordGrants } from './recordGrantsQuery'
import type { RecordAccessEntity } from './recordGrantsQuery'
import type { paths } from '@omnea-app/permissions/permissionsHttpClient/permissionsGeneratedTypes'

type RecordAccessGrantBody = NonNullable<
  paths['/entities/{entity}/{recordId}/access/grant']['post']['requestBody']
>['content']['application/json']

export type RecordAccessGrantChange = RecordAccessGrantBody['changes'][number]
export type RecordGrantConfiguration = Extract<RecordAccessGrantChange, { op: 'grant' }>['configuration']
export type RecordGrantSubject = RecordAccessGrantChange['subject']

export const useUpdateRecordAccessGrant = (entity: RecordAccessEntity, recordId: string) => {
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    // A read issued before this write and landing after it would show the pre-save list.
    onMutate: async (_changes, { client }) => {
      await cancelRecordGrants(client)
    },
    mutationFn: async (changes: RecordAccessGrantChange[]) => {
      const authorization = await getToken()
      const permissionsClient = new PermissionsHttpClient({ authorization })
      const { data, error } = await permissionsClient.POST('/entities/{entity}/{recordId}/access/grant', {
        params: { path: { entity, recordId } },
        body: { changes },
      })
      throwIfApiError(error)
      return data
    },
    // The response names the record only, so the list is re-read rather than patched. A grant also
    // changes what the subject may do, which every record affordance reads.
    onSettled: (_data, _error, _changes, _onMutateResult, { client }) =>
      Promise.all([invalidateRecordGrants(client), invalidateRecordAuthorization(client)]),
  })
}
